import app from '../app';
import { GambitType } from '../query/IGambit';
import AutocompleteReader from '../utils/AutocompleteReader';
import Button from '../components/Button';
export default class GambitsAutocomplete {
  query = '';
  constructor(resource, jqueryInput, onchange, afterSuggest) {
    this.resource = resource;
    this.jqueryInput = jqueryInput;
    this.onchange = onchange;
    this.afterSuggest = afterSuggest;
  }
  suggestions(query) {
    const gambits = app.search.gambits.for(this.resource).filter(gambit => gambit.enabled());
    this.query = query;

    // We group the boolean gambits together to produce an initial item of
    // is:unread,sticky,locked, etc.
    const groupedGambits = gambits.filter(gambit => gambit.type === GambitType.Grouped);
    const keyValueGambits = gambits.filter(gambit => gambit.type !== GambitType.Grouped);
    const uniqueGroups = [];
    for (const gambit of groupedGambits) {
      if (uniqueGroups.includes(gambit.suggestion().group)) continue;
      uniqueGroups.push(gambit.suggestion().group);
    }
    const instancePerGroup = [];
    for (const group of uniqueGroups) {
      instancePerGroup.push({
        type: GambitType.Grouped,
        suggestion: () => ({
          group,
          key: groupedGambits.filter(gambit => gambit.suggestion().group === group).map(gambit => {
            const key = gambit.suggestion().key;
            return key instanceof Array ? key.join(', ') : key;
          }).join(', ')
        }),
        pattern: () => '',
        filterKey: () => '',
        toFilter: () => [],
        fromFilter: () => '',
        predicates: false,
        enabled: () => true
      });
    }
    const autocompleteReader = new AutocompleteReader(null);
    const cursorPosition = this.jqueryInput().prop('selectionEnd') ?? query.length;
    const lastChunk = query.slice(0, cursorPosition);
    const autocomplete = autocompleteReader.check(lastChunk, cursorPosition, /\S+$/);
    let typed = autocomplete?.typed || '';

    // Negative gambits are a thing ;)
    const negative = typed.startsWith('-');
    if (negative) {
      typed = typed.slice(1);
    }
    const colonIndex = typed.indexOf(':');

    // If the query contains a colon, we are typing or exploring specific values from that group.
    if (colonIndex !== -1) {
      const gambitKey = typed.slice(0, colonIndex).toLowerCase();
      const gambitQuery = typed.slice(colonIndex + 1).toLowerCase();
      const specificGambitSuggestions = this.specificGambitSuggestions(gambitKey, gambitQuery, uniqueGroups, groupedGambits, autocomplete, negative);
      if (specificGambitSuggestions) {
        return specificGambitSuggestions;
      }

      // If the user typed a colon for an unknown group or a non-grouped gambit,
      // do not show unrelated top-level gambits.
      return [];
    }
    const normalizedTyped = typed.toLowerCase();

    // This is all the gambit suggestions.
    return [...instancePerGroup, ...keyValueGambits].filter(gambit => {
      if (!normalizedTyped) return true;
      const key = (gambit.type === GambitType.Grouped ? gambit.suggestion().group : gambit.suggestion().key).toLowerCase();
      return key.includes(normalizedTyped);
    }).map(gambit => {
      const suggestion = gambit.suggestion();
      const key = gambit.type === GambitType.Grouped ? suggestion.group : suggestion.key;
      const hint = gambit.type === GambitType.Grouped ? suggestion.key : suggestion.hint;
      return this.gambitSuggestion(key, hint, negated => {
        const isNeg = typeof negated === 'boolean' ? negated : negative;
        const start = autocomplete?.relativeStart ?? cursorPosition;
        const keyPart = autocomplete ? this.query.slice(start).match(/^[^\s:]*:?/)[0] : '';
        this.suggest((isNeg ? '-' : '') + key + ':', keyPart, start);
      });
    });
  }
  specificGambitSuggestions(gambitKey, gambitQuery, uniqueGroups, groupedGambits, autocomplete, negative) {
    if (negative === void 0) {
      negative = false;
    }
    const matchingGroup = uniqueGroups.find(g => g.toLowerCase() === gambitKey.toLowerCase());
    if (matchingGroup) {
      const prefix = (negative ? '-' : '') + matchingGroup + ':';
      const matchingKeys = groupedGambits.filter(gambit => gambit.suggestion().group.toLowerCase() === matchingGroup.toLowerCase()).flatMap(gambit => gambit.suggestion().key instanceof Array ? gambit.suggestion().key : [gambit.suggestion().key]).filter(key => !gambitQuery || key.toLowerCase().startsWith(gambitQuery));
      if (matchingKeys.length === 1 && matchingKeys[0].toLowerCase() === gambitQuery.toLowerCase()) {
        return [];
      }
      return matchingKeys.map(gambit => this.gambitSuggestion(gambit, null, () => this.suggest(prefix + gambit, this.query.slice(autocomplete.relativeStart).match(/^\S*/)[0], autocomplete.relativeStart)));
    }
    return null;
  }
  gambitSuggestion(key, value, suggest) {
    return m("li", null, m("span", {
      className: "Dropdown-item GambitsAutocomplete-gambit"
    }, m("button", {
      type: "button",
      className: "Button--ua-reset",
      onclick: () => suggest()
    }, m("span", {
      className: "GambitsAutocomplete-gambit-key"
    }, key, !!value && ':'), !!value && m("span", {
      className: "GambitsAutocomplete-gambit-value"
    }, value)), !!value && m("span", {
      className: "GambitsAutocomplete-gambit-actions"
    }, m(Button, {
      class: "Button Button--icon",
      onclick: () => suggest(false),
      icon: "fas fa-plus",
      "aria-label": app.translator.trans('core.lib.search.gambit_plus_button_a11y_label')
    }), m(Button, {
      class: "Button Button--icon",
      onclick: () => suggest(true),
      icon: "fas fa-minus",
      "aria-label": app.translator.trans('core.lib.search.gambit_minus_button_a11y_label')
    }))));
  }
  suggest(text, fromTyped, start) {
    const $input = this.jqueryInput();
    const query = this.query;
    const replaced = query.slice(0, start) + text + query.slice(start + fromTyped.length);
    this.onchange(replaced);
    $input[0].focus();
    setTimeout(() => {
      $input[0].setSelectionRange(start + text.length, start + text.length);
      m.redraw();
    }, 50);
    this.afterSuggest(replaced);
  }
}
flarum.reg.add('core', 'common/utils/GambitsAutocomplete', GambitsAutocomplete);